import { useState } from "react";
import { Lock, Pencil, Plus, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { ModalRestricoes } from "@/components/ModalRestricoes";
import { usePermissions } from "@/hooks/usePermissions";
import {
  ROTULO_TIPO,
  ROTULO_TIPO_PLURAL,
  ehTipoProfissional,
  rotuloTipo,
  type TipoProfissional,
} from "@/lib/tipoProfissional";

export function Profissionais() {
  const { isAdmin } = usePermissions();
  const profsQ = trpc.profissionais.listar.useQuery({ incluirInativos: true });
  const utils = trpc.useUtils();
  const [filtroTipo, setFiltroTipo] = useState<"todos" | TipoProfissional>("todos");
  const [editando, setEditando] = useState<{ id: number; nome: string; tipo: string; dataNascimento: string | null } | null>(null);
  const [vinculando, setVinculando] = useState<{ id: number; nome: string } | null>(null);
  const [restricoesPara, setRestricoesPara] = useState<{ id: number; nome: string } | null>(null);
  const [novoOpen, setNovoOpen] = useState(false);

  const linhas = (profsQ.data ?? []).filter(
    (p) => filtroTipo === "todos" || p.tipo === filtroTipo,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Profissionais</h1>
        <div className="flex gap-2">
          <Select value={filtroTipo} onValueChange={(v) => setFiltroTipo(v as typeof filtroTipo)}>
            <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="veterinario">{ROTULO_TIPO_PLURAL.veterinario}</SelectItem>
              <SelectItem value="auxiliar">{ROTULO_TIPO_PLURAL.auxiliar}</SelectItem>
              <SelectItem value="ambos">{ROTULO_TIPO_PLURAL.ambos}</SelectItem>
              <SelectItem value="apoio">{ROTULO_TIPO_PLURAL.apoio}</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button onClick={() => setNovoOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Novo profissional
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Equipe</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Aniversário</TableHead>
                <TableHead>Usuário vinculado</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin && <TableHead className="text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium text-slate-900">{p.nome}</TableCell>
                  <TableCell>
                    <Badge variant={ehTipoProfissional(p.tipo) ? p.tipo : "outline"}>
                      {rotuloTipo(p.tipo)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {p.dataNascimento ? (
                      <span className="text-sm">
                        🎂 {p.dataNascimento.slice(8, 10)}/{p.dataNascimento.slice(5, 7)}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>{p.userEmail ?? <span className="text-slate-400">Sem vínculo</span>}</TableCell>
                  <TableCell>
                    <Badge variant={p.ativo ? "ativo" : "inativo"}>{p.ativo ? "Ativo" : "Inativo"}</Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" title="Restrições" onClick={() => setRestricoesPara({ id: p.id, nome: p.nome })}>
                          <Lock className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Editar" onClick={() => setEditando({ id: p.id, nome: p.nome, tipo: p.tipo, dataNascimento: p.dataNascimento })}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Vincular usuário" onClick={() => setVinculando({ id: p.id, nome: p.nome })}>
                          <Shield className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!linhas.length && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 6 : 5} className="text-center text-slate-400">Nenhum profissional.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editando && (
        <EditarModal
          profissional={editando}
          open={!!editando}
          onClose={() => {
            setEditando(null);
            utils.profissionais.listar.invalidate();
          }}
        />
      )}
      {vinculando && (
        <VincularModal
          profissional={vinculando}
          open={!!vinculando}
          onClose={() => {
            setVinculando(null);
            utils.profissionais.listar.invalidate();
          }}
        />
      )}
      {novoOpen && (
        <NovoProfissionalModal
          open={novoOpen}
          onClose={() => {
            setNovoOpen(false);
            utils.profissionais.listar.invalidate();
          }}
        />
      )}
      {restricoesPara && (
        <ModalRestricoes
          profissionalId={restricoesPara.id}
          profissionalNome={restricoesPara.nome}
          open={!!restricoesPara}
          onOpenChange={(o) => !o && setRestricoesPara(null)}
        />
      )}
    </div>
  );
}

function EditarModal({
  profissional,
  open,
  onClose,
}: {
  profissional: { id: number; nome: string; tipo: string; dataNascimento: string | null };
  open: boolean;
  onClose: () => void;
}) {
  const [nome, setNome] = useState(profissional.nome);
  const [tipo, setTipo] = useState<TipoProfissional>(
    ehTipoProfissional(profissional.tipo) ? profissional.tipo : "auxiliar",
  );
  const [dataNascimento, setDataNascimento] = useState(profissional.dataNascimento ?? "");
  const mut = trpc.profissionais.atualizar.useMutation({ onSuccess: onClose });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Editar profissional</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={tipo} onValueChange={(v) => ehTipoProfissional(v) && setTipo(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="veterinario">{ROTULO_TIPO.veterinario}</SelectItem>
                <SelectItem value="auxiliar">{ROTULO_TIPO.auxiliar}</SelectItem>
                <SelectItem value="ambos">{ROTULO_TIPO.ambos}</SelectItem>
                <SelectItem value="apoio">{ROTULO_TIPO.apoio}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Data de nascimento (opcional)</Label>
            <Input
              type="date"
              value={dataNascimento}
              onChange={(e) => setDataNascimento(e.target.value)}
            />
            <p className="text-xs text-slate-400">
              Se preenchida, o aniversário aparece destacado no calendário todo ano.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() =>
              mut.mutate({
                id: profissional.id,
                nome,
                tipo,
                dataNascimento: dataNascimento ? dataNascimento : null,
              })
            }
            disabled={mut.isPending}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VincularModal({ profissional, open, onClose }: { profissional: { id: number; nome: string }; open: boolean; onClose: () => void }) {
  const [emailBusca, setEmailBusca] = useState("");
  const usersQ = trpc.admin.listarUsuarios.useQuery();
  const mut = trpc.profissionais.vincularUsuario.useMutation({ onSuccess: onClose });
  const candidatos = (usersQ.data ?? []).filter((u) =>
    !emailBusca || u.email.toLowerCase().includes(emailBusca.toLowerCase()),
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Vincular usuário a {profissional.nome}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Buscar por email..." value={emailBusca} onChange={(e) => setEmailBusca(e.target.value)} />
          <ul className="max-h-72 space-y-1 overflow-auto">
            {candidatos.map((u) => (
              <li key={u.id}>
                <button
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => mut.mutate({ id: profissional.id, userId: u.id })}
                >
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email} — {u.role}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => mut.mutate({ id: profissional.id, userId: null })}>Desvincular</Button>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NovoProfissionalModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState<TipoProfissional>("veterinario");
  const [dataNascimento, setDataNascimento] = useState("");
  const mut = trpc.profissionais.criar.useMutation({ onSuccess: onClose });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Novo profissional</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={tipo} onValueChange={(v) => setTipo(v as typeof tipo)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="veterinario">{ROTULO_TIPO.veterinario}</SelectItem>
                <SelectItem value="auxiliar">{ROTULO_TIPO.auxiliar}</SelectItem>
                <SelectItem value="ambos">{ROTULO_TIPO.ambos}</SelectItem>
                <SelectItem value="apoio">{ROTULO_TIPO.apoio}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Data de nascimento (opcional)</Label>
            <Input
              type="date"
              value={dataNascimento}
              onChange={(e) => setDataNascimento(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() =>
              mut.mutate({
                nome,
                tipo,
                dataNascimento: dataNascimento ? dataNascimento : null,
              })
            }
            disabled={mut.isPending || !nome}
          >
            Criar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
